<!-- 
A dropdown list for selecting tags to insert into a notice template.
-->
<template>
  <v-menu v-model="menu" :close-on-content-click="false" offset-y left >
    <v-btn slot="activator" color="primary" flat class="ma-0 pl-2 pr-1">
      <v-icon v-if="icon" class="mr-2">{{ icon }}</v-icon>
      <span class="hidden-xs-only mr-1">{{ label }}</span>
      <our-expand-icon :expanded="menu"/>
    </v-btn>
    <v-card>
      <v-text-field
        v-if="showSearch"
        v-model="search"
        :label="searchLabel"
        autofocus
        prepend-inner-icon="search"
        class="pa-2"
        clearable
        hide-details
        outline
      />
      <div class="scrollable-view">
        <v-hover v-for="(tag, index) in filteredTags" :key="tag.name" :class="{ 'grey lighten-5': index % 2 === 0 }" class="ma-0">
          <v-card
            v-ripple
            slot-scope="{ hover }"
            :color="hover ? 'grey lighten-3' : ''"
            flat tile
            class="pt-2 pl-3 pr-3 pb-2 item"
            @click="selectItem(tag)"
            @keydown.enter="selectItem(tag)"
          >
            <h4 :title="tagText(tag)" class="subheading mb-2">
              {{ tag.displayName }}
            </h4>
            <div class="caption mt-2 mb-1 grey--text text--darken-1 description">
              <div v-if="tag.description">
                {{ tag.description }}
                <span v-if="hasSingleLineExample(tag)" class="caption grey--text">e.g. </span>
                <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
                <span v-if="hasSingleLineExample(tag)" class="font-weight-medium" v-html="tag.value"/>
              </div>
              <div v-if="hasMultiLineExample(tag)" class="pt-2">
                <div class="caption grey--text">Example:</div>
                <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
                <div class="font-weight-medium" v-html="tag.value"/>
              </div>
            </div>
          </v-card>
        </v-hover>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import * as action from "@/store/notice-templates/action-types";

export default {
  mixins: [lookupMixins, ruleMixins],
  props: {
    levyCollectionId: {
      type: Number,
      default: null
    },
    label: {
      type: String,
      default: "Tag"
    },
    forSubject: {
      type: Boolean,
      default: false
    },
    fieldName: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      menu: false,
      search: "",
      showSearch: false
    };
  },
  computed: {
    tagAction() {
      return this.forSubject ? action.GET_SUBJECT_TAGS : action.GET_BODY_TAGS;
    },
    state() {
      return this.forSubject
        ? this.$store.state.noticeTemplates.subjectTags
        : this.$store.state.noticeTemplates.bodyTags;
    },
    tags() {
      return this.state.data;
    },
    filteredTags() {
      const search = this.search ? this.search.toLowerCase() : null;
      return this.tags.filter(
        t =>
          search == null ||
          t.displayName.toLowerCase().indexOf(search) > -1 ||
          t.description.toLowerCase().indexOf(search) > -1 ||
          (t.value && t.value.toLowerCase().indexOf(search) > -1)
      );
    },
    busy() {
      return this.state.loading;
    },
    searchLabel() {
      return this.forSubject ? "Search subject tags" : "Search body tags";
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    menu() {
      if (this.menu) {
        setTimeout(() => (this.showSearch = true), 250);
      } else {
        this.showSearch = false;
      }
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      if (this.state.loading || this.state.loaded) return;
      this.load();
    },
    async load() {
      await this.$store.dispatch(this.tagAction, this.levyCollectionId);
      this.$emit("loaded");
    },
    hasSingleLineExample(tag) {
      return tag.value && tag.value.indexOf("<br/>") == -1;
    },
    hasMultiLineExample(tag) {
      return tag.value && tag.value.indexOf("<br/>") >= 0;
    },
    selectItem(tag) {
      this.$emit("select", this.fieldName, tag);
      this.menu = false;
    },
    tagText(tag) {
      return `{{${tag.name}}}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.content {
  font-size: 16px;
}
.scrollable-view {
  width: 600px;
  max-height: 450px;
  overflow-y: auto;
}
.small_button {
  font-size: 8pt;
}
.item {
  cursor: pointer;
}
.item-icon {
  position: relative;
  top: 3px;
}
.description {
  font-size: 13px !important;
}
</style>